import ActionButton from "@/components/buttons/ActionButton";
import { appendSearchParamsToURL } from "@/utils/utils";
import { Card, Tabs } from "antd";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router";
import { PageSizes } from "../../../../../common/constants";
import { useIsCurrentAgentTypePlus } from "../../../../auth/hooks";
import { CommissionOriginType, CommissionProcessingResult } from "../../enums";
import { CommissionsBatch, CommissionsFilterPageRequest, CommissionsUnit } from "../../types";
import { CommissionsBatchOutputs } from "../CommissionsBatchOutputs";
import { CommissionsBatchOutputsAsZipForm } from "../forms/CommissionsBatchOutputsAsZipForm";
import { CommissionsUnitForm } from "../forms/CommissionsUnitForm";
import { BatchCalculatedCommissionFilterForm } from "../forms/filter/BatchCalculatedCommissionFilterForm";
import { BatchCommissionsTable } from "../tables/BatchCommissionsTable";
import { CommissionsUnitTable } from "../tables/CommissionsUnitTable";

const TAB = {
  UNITS: "units",
  COMMISSIONS: "commissions",
  OUTPUTS: "outputs",
};

const pageSize = PageSizes.LARGE;

interface Props {
  batch: CommissionsBatch;
}

export const CommissionsBatchFinishStep = ({ batch }: Props) => {
  const { t } = useTranslation();

  const navigate = useNavigate();

  const [urlParams] = useSearchParams();
  const urlTabKey = (urlParams.get("tab") as keyof typeof TAB) || TAB.OUTPUTS;

  const [downloadOutputsFormOpen, setDownloadOutputsFormOpen] = useState(false);
  const [unitFormOpen, setUnitFormOpen] = useState(false);
  const [unitToUpdate, setUnitToUpdate] = useState<CommissionsUnit>();
  const [commissionFilterPageRequestData, setCommissionFilterPageRequestData] = useState<CommissionsFilterPageRequest>({
    pageIndex: 0,
    pageSize: pageSize,
    keyword: undefined,
    origin: CommissionOriginType.ALL,
    processingResult: CommissionProcessingResult.ALL,
    postponementReasons: [],
    institutionIds: [],
  });

  const isTopAgentPlus = useIsCurrentAgentTypePlus();

  const handleUpdateClick = (unit: CommissionsUnit) => {
    setUnitFormOpen(true);
    setUnitToUpdate(unit);
  };

  const handleFormCancel = () => {
    setUnitFormOpen(false);
    setUnitToUpdate(undefined);
  };

  return (
    <>
      <Card>
        <Tabs
          activeKey={urlTabKey}
          tabBarExtraContent={
            urlTabKey === TAB.OUTPUTS &&
            !isTopAgentPlus && (
              <ActionButton icon="download" onClick={() => setDownloadOutputsFormOpen(true)}>
                {t("commissions.batch.actions.downloadBatchAttachmentsAsZip")}
              </ActionButton>
            )
          }
          onChange={(key) => navigate(appendSearchParamsToURL({ tab: key }), { replace: true })}
          items={[
            {
              key: TAB.UNITS,
              label: t("commissions.batch.sections.batchUnits"),
              children: <CommissionsUnitTable batch={batch} onUpdateClick={handleUpdateClick} />,
            },
            {
              key: TAB.COMMISSIONS,
              label: t("commissions.batch.sections.batchCommissions"),
              children: (
                <>
                  <BatchCalculatedCommissionFilterForm
                    step={batch.step}
                    onFilterSubmit={(filter) => setCommissionFilterPageRequestData({ ...filter, pageIndex: 0 })}
                    filterPageSize={pageSize}
                  />

                  <BatchCommissionsTable
                    batch={batch}
                    viewType="calculated"
                    filterData={commissionFilterPageRequestData}
                    onPageChange={(pageIndex) =>
                      setCommissionFilterPageRequestData({
                        ...commissionFilterPageRequestData,
                        pageIndex: pageIndex - 1,
                      })
                    }
                  />
                </>
              ),
            },
            {
              key: TAB.OUTPUTS,
              label: t("commissions.batch.sections.batchOutputs"),
              children: <CommissionsBatchOutputs batch={batch} />,
            },
          ]}
        />
      </Card>

      <CommissionsUnitForm batch={batch} unit={unitToUpdate} open={unitFormOpen} onFormCancel={handleFormCancel} />

      <CommissionsBatchOutputsAsZipForm
        batch={batch}
        open={downloadOutputsFormOpen}
        onFormCancel={() => setDownloadOutputsFormOpen(false)}
      />
    </>
  );
};
